import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, homeFor } from "@/lib/auth";
import { FEATURES } from "@/lib/content";

export default function Features() {
  const { isAuthed, user } = useAuth();
  const nav = useNavigate();
  const enter = () => nav(isAuthed && user ? homeFor(user) : "/login");

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-14 pt-10 pb-24">
      <div className="max-w-2xl">
        <div className="overline text-cobalt-500">Capabilities</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-black tracking-tighter">
          Everything a campus help desk needs.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Built for the way institutional IT actually works &mdash; high volume, many categories, strict response times.
        </p>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="glass rounded-2xl p-6"
            >
              <div className="h-11 w-11 rounded-xl bg-cobalt-500/10 text-cobalt-500 grid place-items-center">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-xl font-extrabold tracking-tighter">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.detail}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="glass rounded-3xl p-5 flex items-center gap-4 mt-10">
        <Zap className="h-5 w-5 text-cobalt-500 flex-none" />
        <div className="text-sm">
          <span className="font-medium">Real-time, end to end. </span>
          <span className="text-muted-foreground">Tickets, dashboards, and notifications update live as your team works.</span>
        </div>
      </div>

      <div className="mt-14 glass-strong rounded-3xl p-10 lg:p-12 text-center">
        <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
          Curious how it fits your workflow?
        </h2>
        <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
          See the raise-to-resolve flow in detail, or talk to us about your campus.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/how-it-works">
            <Button variant="outline" size="lg" className="rounded-full font-medium h-11 px-6">
              How it works
            </Button>
          </Link>
          <Button onClick={enter} size="lg"
            className="rounded-full font-medium h-11 px-6 bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100">
            {isAuthed ? "Go to your dashboard" : "Sign in to Solution"} <ArrowUpRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
